// 📁 src/components/VoiceButton.jsx
// 🎙️ Botón de voz para reproducir mensajes con TTS

import React, { useState } from 'react';
import { Volume2, VolumeX, Loader2, AlertCircle } from 'lucide-react';
import { textToSpeech } from '../services/voiceApiService';
import { audioPlayer } from '../utils/audioPlayer';

/**
 * Botón para reproducir texto usando TTS
 * Maneja estados: idle, loading, playing, error
 */
const VoiceButton = ({ text, voice = 'alloy', className = '' }) => {
  console.log('🔔 [VOICE-BUTTON LEGACY] Componente montado para texto:', text?.substring(0, 50));
  
  const [state, setState] = useState('idle'); // idle | loading | playing | error
  const [error, setError] = useState(null);

  const handlePlay = async () => {
    console.log('🎤 [VOICE-BUTTON LEGACY] handlePlay ejecutado');
    
    if (!text || text.trim() === '') {
      return;
    }

    // Si ya está reproduciendo, detener
    if (state === 'playing') {
      console.log('⏹️ [VOICE-BUTTON LEGACY] Deteniendo reproducción');
      audioPlayer.stop();
      setState('idle');
      return;
    }

    setState('loading');
    setError(null);
    
    console.log('🔄 [VOICE-BUTTON LEGACY] Llamando a textToSpeech API...');

    try {
      const startTime = Date.now();
      
      // Solicitar TTS
      const { audioBase64, contentType } = await textToSpeech({
        text,
        voice: 'nova', // Voz femenina enérgica
        format: 'mp3',
        speed: 1.3, // Velocidad aumentada (empresaria apurada)
      });
      
      const duration = Date.now() - startTime;
      console.log(`✅ [VOICE-BUTTON LEGACY] TTS completado en ${duration}ms`);

      // Reproducir
      setState('playing');
      console.log('▶️ [VOICE-BUTTON LEGACY] Reproduciendo con audioPlayer...');
      await audioPlayer.playFromBase64(audioBase64, contentType);
      setState('idle');
      console.log('✅ [VOICE-BUTTON LEGACY] Reproducción completada');
    } catch (err) {
      console.error('❌ [VOICE-BUTTON LEGACY] Error:', err);
      setState('error');
      setError(err.message || 'No se pudo reproducir el audio');
      
      // Volver a idle después de 3 segundos
      setTimeout(() => {
        setState('idle');
        setError(null);
      }, 3000);
    }
  };

  const getButtonContent = () => {
    switch (state) {
      case 'loading':
        return (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="text-xs">Generando...</span>
          </>
        );
      case 'playing':
        return (
          <>
            <VolumeX className="w-3.5 h-3.5" />
            <span className="text-xs">Detener</span>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <span className="text-xs">Reintentar</span>
          </>
        );
      default:
        return (
          <>
            <Volume2 className="w-3.5 h-3.5" />
            <span className="text-xs">Escuchar</span>
          </>
        );
    }
  };

  const getButtonClasses = () => {
    const baseClasses = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${className}`;
    
    switch (state) {
      case 'loading':
        return `${baseClasses} bg-gray-100 text-gray-600 cursor-wait`;
      case 'playing':
        return `${baseClasses} bg-purple-100 text-purple-700 hover:bg-purple-200`;
      case 'error':
        return `${baseClasses} bg-red-100 text-red-700 hover:bg-red-200`;
      default:
        return `${baseClasses} bg-indigo-100 text-indigo-700 hover:bg-indigo-200`;
    }
  };

  return (
    <div className="mt-2">
      <button
        onClick={handlePlay}
        disabled={state === 'loading'}
        className={getButtonClasses()}
        title={error || 'Reproducir mensaje con voz'}
      >
        {getButtonContent()}
      </button>
      {error && state === 'error' && (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
};

export default VoiceButton;
